import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import { KeyboardBackspace } from '@mui/icons-material';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import queryString from 'query-string';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import { ESortDirection, TTableStructure } from 'ui/EasTable/types';
import { TFilters, TOrderBy } from 'redux/types';
import { TAuditLogItem } from 'redux/reducers/changeHistory/types';
import {
	getChangeHistoryByUser,
	getChangeHistoryByUserLoading,
} from 'redux/reducers/changeHistory/selectors';
import { getChangeHistoryByUserRequest } from 'redux/reducers/changeHistory/reducer';
import { getSavedUrl2 } from 'redux/reducers/auth/selectors';
import { PATHS } from 'routes/constants';
import { tableStructure as baseTableStructure } from './tableStructure';
import { UserAvatar } from '../../../ui/UserAvatar';
import { getUsersList } from '../../../redux/reducers/user/selectors';
import { TUser } from '../../../redux/reducers/user/types';
import { getFiltersFromNavigateParams } from '../../../utils/FilterParams';
import { getEMerchantRequest } from '../../../redux/reducers/monthlyFee/reducer';

export const UserChangeHistory = () => {
	const { userId } = useParams<{ userId?: string }>();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);
	const changeHistory = useSelector(getChangeHistoryByUser);
	const changeHistoryLoading = useSelector(getChangeHistoryByUserLoading);
	const savedUrl2 = useSelector(getSavedUrl2);
	const [currentTableStructure, setCurrentTableStructure] =
		useState<TTableStructure<keyof TAuditLogItem>[]>(baseTableStructure);
	const usersList = useSelector(getUsersList);
	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TAuditLogItem>[]) => {
			setCurrentTableStructure(newTableStructure);
		},
		[],
	);

	const { page } = queryString.parse(search);

	// const filters: TFilters = currentTableStructure.reduce((acc, item) => {
	// 	if (!!navigateParams[item.selector as string] && item.type === ETableStructureType.DATE) {
	// 		if (navigateParams[item.selector as string] === DATES_ID.CUSTOM_RANGE) {
	// 			if (navigateParams[`${String(item.selector)}StartDate`]) {
	// 				acc.push({
	// 					fieldName: String(item.selector),
	// 					value: navigateParams[`${String(item.selector)}StartDate`] as string,
	// 					operator: '>=',
	// 				});
	// 			}
	// 			if (navigateParams[`${String(item.selector)}EndDate`]) {
	// 				acc.push({
	// 					fieldName: String(item.selector),
	// 					value: navigateParams[`${String(item.selector)}EndDate`] as string,
	// 					operator: '<=',
	// 				});
	// 			}
	// 			return acc;
	// 		}
	// 		const dates = DATES_ARRAY.find((i) => i.id === navigateParams[item.selector as string]);
	// 		if (dates?.from)
	// 			acc.push({ fieldName: String(item.selector), value: dates.from, operator: '>=' });
	// 		if (dates?.to)
	// 			acc.push({ fieldName: String(item.selector), value: dates.to, operator: '<=' });
	// 		return acc;
	// 	}
	// 	if (navigateParams[item.selector as string])
	// 		acc.push({
	// 			fieldName: String(item.selector),
	// 			value: navigateParams[item.selector as string] as string,
	// 			operator: '=',
	// 		});
	// 	return acc;
	// }, [] as TFilters);

	// if (userId) {
	// 	filters.push({
	// 		fieldName: 'author',
	// 		value: userId,
	// 		operator: '=',
	// 	});
	// }

	// if (navigateParams.searchEmId) {
	// 	filters.push({
	// 		fieldName: 'em_id',
	// 		value: navigateParams.searchEmId as string,
	// 		operator: '=',
	// 	});
	// }

	const filters: TFilters = getFiltersFromNavigateParams(
		currentTableStructure,
		navigateParams,
	) as TFilters;

	useEffect(() => {
		dispatch(getEMerchantRequest());
	}, [dispatch]);

	useEffect(() => {
		if (!userId) return;
		const orderBy: TOrderBy[] = [];
		if (navigateParams?.sortIndex) {
			orderBy.push({
				fieldName: currentTableStructure[+navigateParams.sortIndex]?.selector as string,
				direction:
					navigateParams?.sortDirection && String(navigateParams.sortDirection) === 'desc'
						? ESortDirection.DESC
						: ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.DESC });

		dispatch(
			getChangeHistoryByUserRequest({
				userId: userId,
				body: {
					pageNumber: page ? +page : 1,
					pageSize: PAGE_SIZE,
					searchString: String(navigateParams?.search ?? ''),
					filters,
					orderBy,
				},
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, search, userId]);

	const handleBack = () => {
		if (savedUrl2 && savedUrl2 !== window.location.pathname) {
			if (typeof savedUrl2 === 'string' && savedUrl2.includes(PATHS.USER_MANAGEMENT_PAGE)) {
				navigate(savedUrl2);
				return;
			}
		}
		navigate(PATHS.USER_MANAGEMENT_PAGE);
	};

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
				<IconButton onClick={handleBack} color="primary" sx={{ p: 0 }}>
					<KeyboardBackspace />
				</IconButton>
				<Typography variant="h5" color="primary" fontWeight="bold">
					User Change History
				</Typography>
				{usersList && (
					<UserAvatar
						user={usersList.find((user: TUser) => user.id === String(userId)) ?? null}
						size="sm"
					/>
				)}
			</Box>
			<EasTableWithFilter
				tableStructure={currentTableStructure}
				items={changeHistory}
				itemsIsLoading={changeHistoryLoading}
				handleAction={() => {}}
				handleLink={() => {}}
				skeletonRows={[1, 1, 1, 1, 1, 1, 1, 1]}
				onTableStructureChange={handleTableStructureChange}
				emFilter
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary" fontWeight="bold">
						Change History
					</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						{changeHistoryLoading && <CircularProgress size={20} />}
					</Box>
				</Box>
			</EasTableWithFilter>
		</Box>
	);
};
